import { useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SchemaOrg from "@/components/base/SchemaOrg";
import { businessDetails } from "@/mocks/business";

const siteUrl =
  (import.meta.env.VITE_SITE_URL as string) || "https://tbm-lab.genaiweb.ai";

export default function BusinessDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const project = businessDetails.find((item) => item.slug === slug);

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <section className="w-full pt-32 pb-20 bg-gamo-light-gray">
            <div className="section-container text-center">
              <h1 className="text-gamo-black text-2xl md:text-3xl font-bold">
                お探しの事業ページは見つかりませんでした
              </h1>
              <p className="text-gamo-medium-gray text-sm mt-3">
                リンクが古いか、ページが移動した可能性があります。
              </p>
              <a
                href="/business"
                className="inline-flex items-center gap-2 bg-gamo-green text-white text-sm font-medium px-6 py-3 rounded-full mt-8 whitespace-nowrap transition-all duration-300 hover:bg-gamo-green-dark"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                <span>事業内容一覧へ戻る</span>
              </a>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "ホーム",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "事業内容",
        item: `${siteUrl}/business`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: project.title,
        item: `${siteUrl}/business/${project.slug}`,
      },
    ],
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${project.title} | 株式会社TBM健康美容研究所`,
    description: project.description,
    url: `${siteUrl}/business/${project.slug}`,
    publisher: {
      "@type": "Organization",
      name: "株式会社TBM健康美容研究所",
      url: siteUrl,
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SchemaOrg data={pageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <Header />

      <main>
        {/* Breadcrumb */}
        <div className="section-container pt-24 md:pt-28">
          <nav className="flex items-center gap-2 text-xs text-gamo-medium-gray py-4">
            <a href="/" className="hover:text-gamo-green transition-colors">
              ホーム
            </a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <a
              href="/business"
              className="hover:text-gamo-green transition-colors"
            >
              事業内容
            </a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-gamo-dark-gray">{project.title}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="w-full bg-gamo-light-gray">
          <div className="section-container py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-gamo-green/10 text-gamo-green">
                    <i className={`${project.icon} text-xl`}></i>
                  </div>
                  <span className="text-gamo-green text-xs tracking-[0.15em]">
                    {project.category}
                  </span>
                </div>
                <h1 className="text-gamo-black text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide leading-tight">
                  {project.title}
                </h1>
                <p className="text-gamo-medium-gray text-sm mt-2">
                  {project.en}
                </p>
                <div className="w-10 h-px bg-gamo-green mt-5" />
                <p className="text-gamo-dark-gray text-sm md:text-base leading-relaxed mt-6">
                  {project.summary}
                </p>
              </div>
              <div className="relative h-64 md:h-80 w-full rounded-lg overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  title={`${project.title} | 株式会社TBM健康美容研究所`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Key Points */}
        <section className="w-full bg-white py-10 md:py-14">
          <div className="section-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {project.points.map((point) => (
                <div
                  key={point}
                  className="flex items-start gap-3 bg-gamo-light-gray border border-gamo-border-gray rounded-lg p-4 md:p-5"
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gamo-green/10 text-gamo-green flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <p className="text-gamo-dark-gray text-sm leading-relaxed">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full bg-white py-10 md:py-14">
          <div className="section-container">
            <div className="text-center mb-10">
              <h2 className="text-gamo-black text-lg md:text-xl font-bold">
                サービスの特徴
              </h2>
              <div className="w-10 h-px bg-gamo-green mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {project.features.map((feature) => (
                <div
                  key={feature.title}
                  className="flex flex-col bg-gamo-light-gray border border-gamo-border-gray rounded-lg p-5 md:p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gamo-green/10 text-gamo-green flex-shrink-0">
                      <i className={`${feature.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-gamo-black text-base font-bold leading-snug">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-gamo-medium-gray text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Detail Sections */}
        <section className="w-full bg-white pb-12 md:pb-16">
          <div className="section-container max-w-4xl">
            <div className="space-y-10">
              {project.sections.map((section) => (
                <article
                  key={section.title}
                  className="bg-gamo-light-gray border border-gamo-border-gray rounded-lg p-6 md:p-8"
                >
                  <h2 className="text-gamo-black text-lg md:text-xl font-bold mb-4">
                    {section.title}
                  </h2>
                  <p className="text-gamo-dark-gray text-sm md:text-base leading-relaxed">
                    {section.body}
                  </p>
                  {section.list && (
                    <ul className="mt-5 space-y-3">
                      {section.list.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-3 text-gamo-dark-gray text-sm md:text-base leading-relaxed"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-gamo-green flex-shrink-0 mt-2.5"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-gamo-light-gray py-12 md:py-16">
          <div className="section-container text-center max-w-3xl">
            <h2 className="text-gamo-black text-xl md:text-2xl font-bold">
              {project.title}について詳しく知りたい方へ
            </h2>
            <p className="text-gamo-medium-gray text-sm md:text-base leading-relaxed mt-4">
              お気軽にお問い合わせください。専門スタッフが丁寧にご案内いたします。
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 bg-gamo-green text-white font-medium px-8 py-3.5 rounded-full mt-8 whitespace-nowrap transition-all duration-300 hover:bg-gamo-green-dark"
            >
              <i className="ri-mail-line"></i>
              <span>お問い合わせ</span>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}